import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../../api/axios';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2, FiUsers, FiShield, FiUser, FiMail, FiKey } from 'react-icons/fi';
import { dt, DashLang } from '../../utils/dashboardTranslations';

const RESOURCES: Record<string, string[]> = {
  dashboard: ['view'],
  categories: ['view', 'create', 'edit', 'delete'],
  products: ['view', 'create', 'edit', 'delete'],
  orders: ['view', 'create', 'edit', 'delete', 'cancel', 'refund'],
  pos: ['view', 'sell', 'cancel', 'refund', 'manage_stock'],
  deliveries: ['view', 'view_own', 'edit'],
  branches: ['view', 'create', 'edit', 'delete'],
  delivery_areas: ['view', 'create', 'edit', 'delete'],
  promo_codes: ['view', 'create', 'edit', 'delete'],
  customers: ['view', 'edit'],
  payment_gateways: ['view', 'edit'],
  design: ['view', 'edit'],
  settings: ['view', 'edit'],
  api_access: ['view', 'create', 'edit', 'delete'],
  team: ['view', 'create', 'edit', 'delete'],
  subscription: ['view', 'edit'],
  notifications: ['orders'],
};

const PERMISSION_ACTIONS = ['view', 'view_own', 'create', 'edit', 'delete', 'sell', 'cancel', 'refund', 'manage_stock', 'orders'];

export default function Team() {
  const { store, lang } = useOutletContext<{ store: any; lang: DashLang }>();
  const [tab, setTab] = useState<'roles' | 'users'>('roles');
  const [roles, setRoles] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [posBranches, setPosBranches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Role modal
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [editingRole, setEditingRole] = useState<any>(null);
  const [roleForm, setRoleForm] = useState({ name: '', permissions: {} as Record<string, Record<string, boolean>> });
  const [savingRole, setSavingRole] = useState(false);

  // User modal (add)
  const [showUserModal, setShowUserModal] = useState(false);
  const [userForm, setUserForm] = useState({ first_name: '', last_name: '', email: '', phone: '', role_id: '', preferred_lang: 'ar', pos_all_branches: true, pos_branch_ids: [] as number[] });
  const [savingUser, setSavingUser] = useState(false);

  // Edit user modal
  const [showEditUser, setShowEditUser] = useState<any>(null);
  const [editUserForm, setEditUserForm] = useState({ first_name: '', last_name: '', phone: '', role_id: '', is_active: true, preferred_lang: 'ar', pos_all_branches: true, pos_branch_ids: [] as number[] });
  const [savingEditUser, setSavingEditUser] = useState(false);
  const [sendingReset, setSendingReset] = useState<number | null>(null);

  useEffect(() => {
    if (store?.id) {
      loadData();
    }
  }, [store?.id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [rolesRes, usersRes, branchesRes] = await Promise.all([
        api.get(`/stores/${store.id}/roles`),
        api.get(`/stores/${store.id}/team`),
        api.get(`/stores/${store.id}/team-pos-branches`),
      ]);
      setRoles(rolesRes.data);
      setUsers(usersRes.data);
      setPosBranches(branchesRes.data);
    } catch {} finally {
      setLoading(false);
    }
  };

  // ─── Role handlers ───

  const openRoleModal = (role?: any) => {
    if (role) {
      setEditingRole(role);
      setRoleForm({ name: role.name, permissions: role.permissions || {} });
    } else {
      setEditingRole(null);
      setRoleForm({ name: '', permissions: {} });
    }
    setShowRoleModal(true);
  };

  const togglePerm = (resource: string, action: string) => {
    setRoleForm(prev => {
      const perms = { ...prev.permissions };
      if (!perms[resource]) perms[resource] = {};
      perms[resource] = { ...perms[resource], [action]: !perms[resource][action] };
      return { ...prev, permissions: perms };
    });
  };

  const toggleAllResource = (resource: string) => {
    const actions = RESOURCES[resource];
    const allChecked = actions.every(a => roleForm.permissions[resource]?.[a]);
    setRoleForm(prev => {
      const perms = { ...prev.permissions };
      perms[resource] = {};
      actions.forEach(a => { perms[resource][a] = !allChecked; });
      return { ...prev, permissions: perms };
    });
  };

  const saveRole = async () => {
    if (!roleForm.name.trim()) return;
    setSavingRole(true);
    try {
      if (editingRole) {
        await api.put(`/stores/${store.id}/roles/${editingRole.id}`, roleForm);
        toast.success(dt('role_updated', lang));
      } else {
        await api.post(`/stores/${store.id}/roles`, roleForm);
        toast.success(dt('role_created', lang));
      }
      setShowRoleModal(false);
      loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error');
    } finally {
      setSavingRole(false);
    }
  };

  const deleteRole = async (role: any) => {
    if (!window.confirm(dt('delete_role_confirm', lang))) return;
    try {
      await api.delete(`/stores/${store.id}/roles/${role.id}`);
      toast.success(dt('role_deleted', lang));
      loadData();
    } catch {}
  };

  // ─── User handlers ───

  const openUserModal = () => {
    setUserForm({ first_name: '', last_name: '', email: '', phone: '', role_id: '', preferred_lang: 'ar', pos_all_branches: true, pos_branch_ids: [] });
    setShowUserModal(true);
  };

  const saveUser = async () => {
    setSavingUser(true);
    try {
      await api.post(`/stores/${store.id}/team`, {
        ...userForm,
        role_id: userForm.role_id || null,
        preferred_lang: userForm.preferred_lang,
      });
      toast.success(dt('user_added', lang));
      setShowUserModal(false);
      loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error');
    } finally {
      setSavingUser(false);
    }
  };

  const updateUserRole = async (su: any, roleId: string) => {
    try {
      await api.put(`/stores/${store.id}/team/${su.id}`, { role_id: roleId || null });
      toast.success(dt('user_updated', lang));
      loadData();
    } catch {}
  };

  const toggleUserActive = async (su: any) => {
    try {
      await api.put(`/stores/${store.id}/team/${su.id}`, { is_active: !su.is_active });
      toast.success(dt('user_updated', lang));
      loadData();
    } catch {}
  };

  const openEditUser = (su: any) => {
    setEditUserForm({
      first_name: su.first_name || (su.name || '').split(' ')[0] || '',
      last_name: su.last_name || (su.name || '').split(' ').slice(1).join(' ') || '',
      phone: su.phone || '',
      role_id: su.role_id || '',
      is_active: su.is_active,
      preferred_lang: su.preferred_lang || 'ar',
      pos_all_branches: su.pos_all_branches !== false,
      pos_branch_ids: su.pos_branch_ids || [],
    });
    setShowEditUser(su);
  };

  const saveEditUser = async () => {
    if (!showEditUser) return;
    setSavingEditUser(true);
    try {
      await api.put(`/stores/${store.id}/team/${showEditUser.id}`, {
        first_name: editUserForm.first_name,
        last_name: editUserForm.last_name,
        phone: editUserForm.phone || null,
        role_id: editUserForm.role_id || null,
        is_active: editUserForm.is_active,
        preferred_lang: editUserForm.preferred_lang,
        pos_all_branches: editUserForm.pos_all_branches,
        pos_branch_ids: editUserForm.pos_branch_ids,
      });
      toast.success(dt('user_updated', lang));
      setShowEditUser(null);
      loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error');
    } finally {
      setSavingEditUser(false);
    }
  };

  const sendPasswordReset = async (su: any) => {
    if (!window.confirm(lang === 'ar'
      ? `إرسال رابط إعادة تعيين كلمة المرور إلى ${su.email}؟`
      : `Send password reset link to ${su.email}?`
    )) return;
    setSendingReset(su.id);
    try {
      await api.post(`/stores/${store.id}/team/${su.id}/reset-password`);
      toast.success(lang === 'ar' ? 'تم إرسال رابط إعادة التعيين' : 'Password reset link sent');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error');
    } finally {
      setSendingReset(null);
    }
  };

  const removeUser = async (su: any) => {
    if (!window.confirm(dt('delete_user_confirm', lang))) return;
    try {
      await api.delete(`/stores/${store.id}/team/${su.id}`);
      toast.success(dt('user_removed', lang));
      loadData();
    } catch {}
  };

  if (loading) {
    return <div className="page-loading">{dt('loading', lang)}</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 className="page-title">{dt('team', lang)}</h1>
        <button className="btn btn-primary" onClick={() => tab === 'roles' ? openRoleModal() : openUserModal()}>
          <FiPlus /> {tab === 'roles' ? dt('add_role', lang) : dt('add_user', lang)}
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '2px solid var(--gray-100)' }}>
        <button
          onClick={() => setTab('roles')}
          style={{
            padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer', fontFamily: 'inherit',
            fontSize: 14, fontWeight: 600, color: tab === 'roles' ? 'var(--primary)' : 'var(--gray-400)',
            borderBottom: tab === 'roles' ? '2px solid var(--primary)' : '2px solid transparent', marginBottom: -2,
          }}
        >
          <FiShield style={{ marginBottom: -2 }} /> {dt('roles', lang)} ({roles.length})
        </button>
        <button
          onClick={() => setTab('users')}
          style={{
            padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer', fontFamily: 'inherit',
            fontSize: 14, fontWeight: 600, color: tab === 'users' ? 'var(--primary)' : 'var(--gray-400)',
            borderBottom: tab === 'users' ? '2px solid var(--primary)' : '2px solid transparent', marginBottom: -2,
          }}
        >
          <FiUsers style={{ marginBottom: -2 }} /> {dt('users', lang)} ({users.length})
        </button>
      </div>

      {/* ─── Roles Tab ─── */}
      {tab === 'roles' && (
        loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--gray-400)' }}><p>{dt('loading', lang)}...</p></div>
        ) : roles.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--gray-400)' }}>
            <FiShield size={40} style={{ marginBottom: 12 }} />
            <p style={{ fontSize: 16, fontWeight: 600, margin: '0 0 6px' }}>{dt('no_roles', lang)}</p>
            <p style={{ fontSize: 13 }}>{dt('create_first_role', lang)}</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
            {roles.map((role: any) => (
              <div key={role.id} className="card" style={{ padding: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 16 }}>{role.name}</h3>
                    <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>
                      {role.store_users_count || 0} {dt('members', lang)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-sm" onClick={() => openRoleModal(role)} title="Edit"><FiEdit2 size={14} /></button>
                    <button className="btn btn-sm btn-danger" onClick={() => deleteRole(role)} title="Delete"><FiTrash2 size={14} /></button>
                  </div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                  {Object.entries(role.permissions || {}).filter(([, actions]: any) =>
                    Object.values(actions).some(Boolean)
                  ).map(([res]) => dt(`resource_${res}`, lang)).join(', ') || '—'}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* ─── Users Tab ─── */}
      {tab === 'users' && (
        loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--gray-400)' }}><p>{dt('loading', lang)}...</p></div>
        ) : users.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--gray-400)' }}>
            <FiUsers size={40} style={{ marginBottom: 12 }} />
            <p style={{ fontSize: 16, fontWeight: 600, margin: '0 0 6px' }}>{dt('no_users', lang)}</p>
            <p style={{ fontSize: 13 }}>{dt('add_first_user', lang)}</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
            {users.map((su: any) => (
              <div key={su.id} className="card" style={{ padding: 20, position: 'relative' }}>
                {/* Status badge */}
                <span style={{
                  position: 'absolute', top: 14, insetInlineEnd: 14,
                  padding: '3px 10px', borderRadius: 50, fontSize: 11, fontWeight: 600,
                  background: su.is_active ? '#dcfce7' : '#fee2e2',
                  color: su.is_active ? '#16a34a' : '#dc2626',
                }}>
                  {su.is_active ? dt('active', lang) : dt('inactive', lang)}
                </span>

                {/* User info */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: '50%', background: 'var(--primary)',
                    color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16, fontWeight: 700, flexShrink: 0,
                  }}>
                    {su.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>{su.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--gray-400)', direction: 'ltr', textAlign: lang === 'ar' ? 'right' : 'left' }}>{su.email}</div>
                  </div>
                </div>

                {/* Details */}
                <div style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 14 }}>
                  {su.phone && (
                    <div style={{ marginBottom: 4, direction: 'ltr', textAlign: lang === 'ar' ? 'right' : 'left' }}>
                      {su.phone}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{
                      display: 'inline-block', padding: '2px 10px', borderRadius: 6,
                      background: su.role_name ? '#ede9fe' : '#f3f4f6',
                      color: su.role_name ? '#7c3aed' : '#6b7280',
                      fontSize: 12, fontWeight: 600,
                    }}>
                      {su.role_name || (lang === 'ar' ? 'بدون دور' : 'No Role')}
                    </span>
                    <span style={{
                      display: 'inline-block', padding: '2px 10px', borderRadius: 6,
                      background: '#f0f9ff', color: '#0369a1',
                      fontSize: 12, fontWeight: 600,
                    }}>
                      {su.preferred_lang === 'en' ? 'EN' : 'AR'}
                    </span>
                    <span style={{
                      display: 'inline-block', padding: '2px 10px', borderRadius: 6,
                      background: '#f0fdf4', color: '#15803d', fontSize: 12, fontWeight: 600,
                    }}>
                      {su.pos_all_branches
                        ? (lang === 'ar' ? 'كل فروع POS' : 'All POS branches')
                        : `${su.pos_branch_ids?.length || 0} ${lang === 'ar' ? 'فرع POS' : 'POS branches'}`}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 6, borderTop: '1px solid var(--gray-100)', paddingTop: 12 }}>
                  <button className="btn btn-sm" onClick={() => openEditUser(su)} title={dt('edit', lang)} style={{ flex: 1 }}>
                    <FiEdit2 size={13} style={{ marginInlineEnd: 4 }} /> {dt('edit', lang)}
                  </button>
                  <button
                    className="btn btn-sm"
                    onClick={() => sendPasswordReset(su)}
                    disabled={sendingReset === su.id}
                    title={lang === 'ar' ? 'إرسال رابط تعيين كلمة المرور' : 'Send password reset'}
                    style={{ flex: 1 }}
                  >
                    <FiKey size={13} style={{ marginInlineEnd: 4 }} />
                    {sendingReset === su.id ? '...' : (lang === 'ar' ? 'كلمة المرور' : 'Reset Pass')}
                  </button>
                  <button className="btn btn-sm btn-danger" onClick={() => removeUser(su)} title={dt('delete', lang)}>
                    <FiTrash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* ─── Role Modal ─── */}
      {showRoleModal && (
        <div className="modal-overlay" onClick={() => setShowRoleModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 700 }}>
            <h2>{editingRole ? dt('edit_role', lang) : dt('add_role', lang)}</h2>

            <div className="form-group">
              <label className="form-label">{dt('role_name', lang)} *</label>
              <input
                className="form-input"
                value={roleForm.name}
                onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                placeholder={lang === 'ar' ? 'مثال: موظف مبيعات' : 'e.g. Sales Person'}
                autoFocus
              />
            </div>

            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <label className="form-label" style={{ margin: 0 }}>{dt('permissions', lang)}</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    type="button"
                    onClick={() => {
                      const all: Record<string, Record<string, boolean>> = {};
                      Object.entries(RESOURCES).forEach(([r, actions]) => { all[r] = {}; actions.forEach(a => { all[r][a] = true; }); });
                      setRoleForm(prev => ({ ...prev, permissions: all }));
                    }}
                    style={{ padding: '4px 10px', fontSize: 11, borderRadius: 6, border: '1px solid var(--gray-200)', background: 'white', cursor: 'pointer', fontFamily: 'inherit' }}
                  >
                    {lang === 'ar' ? 'تحديد الكل' : 'Select All'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setRoleForm(prev => ({ ...prev, permissions: {} }))}
                    style={{ padding: '4px 10px', fontSize: 11, borderRadius: 6, border: '1px solid var(--gray-200)', background: 'white', cursor: 'pointer', fontFamily: 'inherit' }}
                  >
                    {lang === 'ar' ? 'إلغاء تحديد الكل' : 'Uncheck All'}
                  </button>
                </div>
              </div>
              <div style={{ border: '1px solid var(--gray-200)', borderRadius: 8, overflowX: 'auto' }}>
                <table style={{ width: '100%', minWidth: 930, borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--gray-50)' }}>
                      <th style={{ padding: '10px 14px', textAlign: lang === 'ar' ? 'right' : 'left', fontWeight: 600 }}>{dt('name', lang)}</th>
                      {PERMISSION_ACTIONS.map(action => (
                        <th key={action} style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 600, width: 70 }}>
                          {dt(`perm_${action}`, lang)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(RESOURCES).map(([resource, actions]) => (
                      <tr key={resource} style={{ borderTop: '1px solid var(--gray-100)' }}>
                        <td style={{ padding: '10px 14px' }}>
                          <button
                            onClick={() => toggleAllResource(resource)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, fontFamily: 'inherit', fontSize: 13, padding: 0, color: 'inherit' }}
                          >
                            {dt(`resource_${resource}`, lang)}
                          </button>
                        </td>
                        {PERMISSION_ACTIONS.map(action => (
                          <td key={action} style={{ padding: '10px 8px', textAlign: 'center' }}>
                            {actions.includes(action) ? (
                              <input
                                type="checkbox"
                                checked={!!roleForm.permissions[resource]?.[action]}
                                onChange={() => togglePerm(resource, action)}
                                style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--primary)' }}
                              />
                            ) : (
                              <span style={{ color: 'var(--gray-200)' }}>—</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button className="btn" onClick={() => setShowRoleModal(false)}>{dt('cancel', lang)}</button>
              <button className="btn btn-primary" onClick={saveRole} disabled={savingRole || !roleForm.name.trim()}>
                {savingRole ? '...' : (editingRole ? dt('update', lang) : dt('create', lang))}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Add User Modal ─── */}
      {showUserModal && (
        <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <h2>{dt('add_user', lang)}</h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">{dt('first_name', lang)} *</label>
                <input className="form-input" value={userForm.first_name} onChange={(e) => setUserForm({ ...userForm, first_name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="form-label">{dt('last_name', lang)} *</label>
                <input className="form-input" value={userForm.last_name} onChange={(e) => setUserForm({ ...userForm, last_name: e.target.value })} required />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{dt('email', lang)} *</label>
              <input type="email" className="form-input" dir="ltr" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} required />
            </div>
            <div className="form-group">
              <label className="form-label">{dt('phone', lang)}</label>
              <input className="form-input" dir="ltr" value={userForm.phone} onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">{dt('role', lang)}</label>
                <select className="form-input" value={userForm.role_id} onChange={(e) => setUserForm({ ...userForm, role_id: e.target.value })}>
                  <option value="">{dt('select_role', lang)}</option>
                  {roles.map((r: any) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{lang === 'ar' ? 'لغة الإشعارات' : 'Notification Language'}</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => setUserForm({ ...userForm, preferred_lang: 'ar' })}
                    style={{
                      flex: 1, padding: '8px 12px', borderRadius: 8, border: '2px solid',
                      borderColor: userForm.preferred_lang === 'ar' ? 'var(--primary)' : 'var(--gray-200)',
                      background: userForm.preferred_lang === 'ar' ? '#ede9fe' : 'white',
                      color: userForm.preferred_lang === 'ar' ? 'var(--primary)' : 'var(--gray-400)',
                      cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'inherit',
                    }}
                  >
                    العربية
                  </button>
                  <button
                    type="button"
                    onClick={() => setUserForm({ ...userForm, preferred_lang: 'en' })}
                    style={{
                      flex: 1, padding: '8px 12px', borderRadius: 8, border: '2px solid',
                      borderColor: userForm.preferred_lang === 'en' ? 'var(--primary)' : 'var(--gray-200)',
                      background: userForm.preferred_lang === 'en' ? '#ede9fe' : 'white',
                      color: userForm.preferred_lang === 'en' ? 'var(--primary)' : 'var(--gray-400)',
                      cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'inherit',
                    }}
                  >
                    English
                  </button>
                </div>
              </div>
            </div>

            <BranchAccessFields
              branches={posBranches}
              value={userForm}
              onChange={setUserForm}
              lang={lang}
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button className="btn" onClick={() => setShowUserModal(false)}>{dt('cancel', lang)}</button>
              <button
                className="btn btn-primary"
                onClick={saveUser}
                disabled={savingUser || !userForm.first_name.trim() || !userForm.last_name.trim() || !userForm.email.trim()}
              >
                {savingUser ? '...' : dt('create', lang)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Edit User Modal ─── */}
      {showEditUser && (
        <div className="modal-overlay" onClick={() => setShowEditUser(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <h2>{dt('edit_user', lang)}</h2>
            <p style={{ fontSize: 13, color: 'var(--gray-400)', margin: '-8px 0 16px', direction: 'ltr', textAlign: lang === 'ar' ? 'right' : 'left' }}>{showEditUser.email}</p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">{dt('first_name', lang)} *</label>
                <input className="form-input" value={editUserForm.first_name} onChange={(e) => setEditUserForm({ ...editUserForm, first_name: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">{dt('last_name', lang)} *</label>
                <input className="form-input" value={editUserForm.last_name} onChange={(e) => setEditUserForm({ ...editUserForm, last_name: e.target.value })} />
              </div>
            </div>
            <BranchAccessFields
              branches={posBranches}
              value={editUserForm}
              onChange={setEditUserForm}
              lang={lang}
            />
            <div className="form-group">
              <label className="form-label">{dt('phone', lang)}</label>
              <input className="form-input" dir="ltr" value={editUserForm.phone} onChange={(e) => setEditUserForm({ ...editUserForm, phone: e.target.value })} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">{dt('role', lang)}</label>
                <select className="form-input" value={editUserForm.role_id} onChange={(e) => setEditUserForm({ ...editUserForm, role_id: e.target.value })}>
                  <option value="">{dt('no_role', lang)}</option>
                  {roles.map((r: any) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{lang === 'ar' ? 'لغة الإشعارات' : 'Notification Language'}</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => setEditUserForm({ ...editUserForm, preferred_lang: 'ar' })}
                    style={{
                      flex: 1, padding: '8px 12px', borderRadius: 8, border: '2px solid',
                      borderColor: editUserForm.preferred_lang === 'ar' ? 'var(--primary)' : 'var(--gray-200)',
                      background: editUserForm.preferred_lang === 'ar' ? '#ede9fe' : 'white',
                      color: editUserForm.preferred_lang === 'ar' ? 'var(--primary)' : 'var(--gray-400)',
                      cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'inherit',
                    }}
                  >
                    العربية
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditUserForm({ ...editUserForm, preferred_lang: 'en' })}
                    style={{
                      flex: 1, padding: '8px 12px', borderRadius: 8, border: '2px solid',
                      borderColor: editUserForm.preferred_lang === 'en' ? 'var(--primary)' : 'var(--gray-200)',
                      background: editUserForm.preferred_lang === 'en' ? '#ede9fe' : 'white',
                      color: editUserForm.preferred_lang === 'en' ? 'var(--primary)' : 'var(--gray-400)',
                      cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'inherit',
                    }}
                  >
                    English
                  </button>
                </div>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{dt('status', lang)}</label>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setEditUserForm({ ...editUserForm, is_active: true })}
                  style={{
                    flex: 1, padding: '8px 12px', borderRadius: 8, border: '2px solid',
                    borderColor: editUserForm.is_active ? '#16a34a' : 'var(--gray-200)',
                    background: editUserForm.is_active ? '#dcfce7' : 'white',
                    color: editUserForm.is_active ? '#16a34a' : 'var(--gray-400)',
                    cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'inherit',
                  }}
                >
                  {dt('active', lang)}
                </button>
                <button
                  type="button"
                  onClick={() => setEditUserForm({ ...editUserForm, is_active: false })}
                  style={{
                    flex: 1, padding: '8px 12px', borderRadius: 8, border: '2px solid',
                    borderColor: !editUserForm.is_active ? '#dc2626' : 'var(--gray-200)',
                    background: !editUserForm.is_active ? '#fee2e2' : 'white',
                    color: !editUserForm.is_active ? '#dc2626' : 'var(--gray-400)',
                    cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'inherit',
                  }}
                >
                  {dt('inactive', lang)}
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button className="btn" onClick={() => setShowEditUser(null)}>{dt('cancel', lang)}</button>
              <button
                className="btn btn-primary"
                onClick={saveEditUser}
                disabled={savingEditUser || !editUserForm.first_name.trim() || !editUserForm.last_name.trim()}
              >
                {savingEditUser ? '...' : dt('update', lang)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BranchAccessFields({ branches, value, onChange, lang }: {
  branches: any[];
  value: { pos_all_branches: boolean; pos_branch_ids: number[] };
  onChange: React.Dispatch<React.SetStateAction<any>>;
  lang: DashLang;
}) {
  const toggleBranch = (branchId: number) => {
    const selected = value.pos_branch_ids.includes(branchId);
    onChange((current: any) => ({
      ...current,
      pos_branch_ids: selected
        ? current.pos_branch_ids.filter((id: number) => id !== branchId)
        : [...current.pos_branch_ids, branchId],
    }));
  };

  return (
    <div className="form-group" style={{ padding: 14, border: '1px solid var(--gray-200)', borderRadius: 10 }}>
      <label className="form-label" style={{ marginBottom: 10 }}>
        {lang === 'ar' ? 'الفروع المسموح بها في نقطة البيع' : 'Allowed POS branches'}
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
        <input
          type="checkbox"
          checked={value.pos_all_branches}
          onChange={(e) => onChange((current: any) => ({ ...current, pos_all_branches: e.target.checked, pos_branch_ids: e.target.checked ? [] : current.pos_branch_ids }))}
          style={{ width: 17, height: 17, accentColor: 'var(--primary)' }}
        />
        {lang === 'ar' ? 'السماح بجميع الفروع الحالية والمستقبلية' : 'Allow all current and future branches'}
      </label>
      {!value.pos_all_branches && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginTop: 12 }}>
          {branches.length === 0 ? (
            <span style={{ color: 'var(--gray-400)', fontSize: 12 }}>{lang === 'ar' ? 'لا توجد فروع' : 'No branches available'}</span>
          ) : branches.map((branch: any) => (
            <label key={branch.id} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 10px', borderRadius: 8, background: value.pos_branch_ids.includes(branch.id) ? '#ede9fe' : 'var(--gray-50)', opacity: branch.is_active ? 1 : .55, cursor: 'pointer', fontSize: 12 }}>
              <input
                type="checkbox"
                checked={value.pos_branch_ids.includes(branch.id)}
                onChange={() => toggleBranch(branch.id)}
                style={{ accentColor: 'var(--primary)' }}
              />
              <span>{lang === 'ar' ? branch.name_ar || branch.name : branch.name}</span>
              {!branch.is_active && <small>({lang === 'ar' ? 'غير نشط' : 'inactive'})</small>}
            </label>
          ))}
        </div>
      )}
      {!value.pos_all_branches && value.pos_branch_ids.length === 0 && (
        <p style={{ margin: '9px 0 0', color: '#b45309', fontSize: 11 }}>
          {lang === 'ar' ? 'لن يتمكن المستخدم من فتح أي صندوق POS.' : 'The user will not be able to open any POS register.'}
        </p>
      )}
    </div>
  );
}
